import styles from './Patroller.module.css';

import imageCompression from 'browser-image-compression';

import Avatar from '@mui/material/Avatar';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from "@mui/material/Button";
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Divider from '@mui/material/Divider';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import CameraAlt from '@mui/icons-material/CameraAlt';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Edit from '@mui/icons-material/Edit';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';


import React from "react";

import { DateTime } from 'luxon';

import { uploadPhoto, verifyPhone } from "../../api.js";

import { Notifications } from '../Notifications/Notifications';
import { useAuth } from "../../AuthContext";

export const Patroller = ({ patroller, userIsAdmin, onSave }) => {
    const [ error, setError ] = React.useState(null);
    const [ user, setUser ] = React.useState(patroller);
    const [ aboutMe, setAboutMe ] = React.useState(decodeURIComponent(patroller?.aboutMe) || '');
    const [ requiredFields, setRequiredFields ] = React.useState([]);
    const [ photo, setPhoto ] = React.useState(null);
    const [ preview, setPreview ] = React.useState(null);
    const [ editable, setEditable ] = React.useState(false);
    const [ editAction, setEditAction ] = React.useState('cancel');
    const [ phoneState, setPhoneState ] = React.useState({
        isValid: false,
        isVerified: patroller?.phoneVerified || false,
        smsConsent: patroller?.smsConsent || false,
        verificationSent: false,
        verificationCode: '',
        isVerifying: false,
        changeRequested: false,
        changeAllowed: false
    });

    const { auth, token } = useAuth();
    
    const photoFileRef = React.useRef(null);

    const userIsSelf = auth.currentUser.uid === patroller.uid;
    const userPrivacyInfo = patroller.privacy;
    
    const displayName = `${ user?.firstName || '' } ${ user?.lastName || '' }`.trim();

    // Helper functions for phone state management
    const updatePhoneState = (updates) => {
        setPhoneState(prev => ({ ...prev, ...updates }));
        setUser(prev => ({ ...prev, phoneVerified: updates.isVerified !== undefined ? updates.isVerified : prev.phoneVerified }));
    };

    // Enhanced validation with rate limiting awareness
    const validatePhoneNumber = React.useCallback((phoneNumber) => {
        if (!phoneNumber || phoneNumber.trim() === '') {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Phone number is required.');

            updatePhoneState({ isValid: false });

            return false;
        }

        const phonePlusOne = /^\+1/.test(phoneNumber);
        const fullNumber = phonePlusOne ? phoneNumber : `+1${ phoneNumber }`;
        const isValid = /^(\+1)\d{10}$/.test(fullNumber);

        if (isValid) {
            setRequiredFields(prev => prev.filter(field => field !== 'phoneNumber'));
            setError(null);
            setUser(prev => ({ ...prev, phoneNumber: fullNumber }));

            updatePhoneState({ isValid: true });

            return true;
        } else {
            setRequiredFields(prev => [ ...prev.filter(field => field !== 'phoneNumber'), 'phoneNumber' ]);
            setError('Invalid phone number format. Must be 10 digits.');

            updatePhoneState({ isValid: false });

            return false;
        }
    }, [ ]);

    const canSave = React.useMemo(() => {
        if (!editable || editAction !== 'save') return false;

        if (userIsAdmin && !userIsSelf) {
            // Admins can save if no required fields OR only 'disabled' changed
            return requiredFields.length === 0;
        }

        if (userIsSelf) {
            // Self-users need no required fields AND verified phone
            return requiredFields.length === 0 && phoneState.isVerified;
        }

        return false; // Non-admin, non-self can't edit
    }, [ editable, editAction, requiredFields, userIsAdmin, userIsSelf, phoneState.isVerified ]);

    React.useEffect(() => {
        setUser(patroller);
        setPhoneState(prev => ({
            ...prev,
            isVerified: patroller?.phoneVerified || false,
            smsConsent: patroller?.smsConsent || false
        }));
    }, [ patroller ]);

    // MARK: Effects
    React.useEffect(() => {
        const missing = [];

        if (!user.firstName) missing.push('firstName');
        if (!user.lastName) missing.push('lastName');
        if (!user.nspNumber) missing.push('nspNumber');
        if (!user.phoneNumber) missing.push('phoneNumber');
        if (!user.startDate) missing.push('startDate');
        if (!user.city) missing.push('city');
        if (!user.zipCode) missing.push('zipCode');

        setRequiredFields(missing);
    }, [ patroller.uid, user.firstName, user.lastName, user.nspNumber, user.phoneNumber, user.startDate, user.city, user.zipCode ]);

    React.useEffect(() => {
        if (phoneState.changeAllowed) {
            updatePhoneState({ isVerified: false, smsConsent: false });
        }
    }, [ phoneState.changeAllowed ]);

    React.useEffect(() => {
        setPreview(user?.photoURL ? user.photoURL : `https://patrol360.dev/uploads/profile_s.png`);
    }, [ user?.photoURL ]);

    React.useEffect(() => {
        setEditAction(editable ? 'cancel' : 'edit');
    }, [ editable ]);

    React.useEffect(() => {
        validatePhoneNumber(user.phoneNumber || '');
    }, [ user.phoneNumber, validatePhoneNumber ]);

    // Early return if patroller is not available or user is disabled and not admin
    if (!patroller || (!userIsAdmin && patroller.disabled)) return null;

    // MARK: Handlers
    const handleEditPhoto = () => {
        editable && photoFileRef.current.click();
    };

    const handleOnFileChange = async (event) => {
        const file = event.target.files[ 0 ];

        if (!file || !file.type.match('image.*')) {
            setError('Please select a valid image file.');

            return;
        }

        try {
            const options = {
                maxWidthOrHeight: 400,
                useWebWorker: true,
                fileType: 'image/png'
            };

            const compressedFile = await imageCompression(file, options);
            const reader = new FileReader();

            reader.onloadend = () => {
                setPreview(reader.result);
            };

            reader.readAsDataURL(compressedFile);

            setPhoto(compressedFile);
            setEditAction('save');
        } catch (error) {
            setError('Failed to process image');

            console.error('Image compression error:', error);
        }
    };

    const handleAdminChange = async (e) => {
        const { name, checked } = e.target;

        if (userIsAdmin && !userIsSelf) {
            const role = checked && name === 'admin' ? 'admin' : 'user';

            setUser(prev => ({ ...prev, role, admin: checked, scheduler: false }));
            setEditAction('save');
        }
    };

    const handleSchedulerChange = async (e) => {
        const { name, checked } = e.target;

        if (userIsAdmin && !userIsSelf && !user.admin) {
            const role = checked && name === 'scheduler' ? 'scheduler' : 'user';

            setUser(prev => ({ ...prev, role, scheduler: checked }));
            setEditAction('save');
        }
    };

    const handleAboutMeChange = (e) => {
        const { value } = e.target;

        setAboutMe(value.slice(0, 300));
        setEditAction('save');
    };

    const handleOnChange = (e) => {
        const { name, value, type, checked, required } = e.target;
        const fieldValue = type === 'checkbox' ? checked : value;

        setUser(prev => ({ ...prev, [ name ]: fieldValue }));

        if (name === 'phoneNumber') {
            const isValid = validatePhoneNumber(fieldValue);

            if (!isValid) {
                return;
            }
        }

        if (name === 'startDate') {
            // Validate any valid date format (basic check)
            const date = DateTime.fromISO(fieldValue);

            if (fieldValue && !date.isValid) {
                setRequiredFields(prev => [ ...prev.filter(field => field !== name), name ]);
                setError('Invalid date format.');

                return;
            }
        }

        // Handle required field validation
        if (required && !fieldValue) {
            setRequiredFields(prev => [ ...prev.filter(field => field !== name), name ]);
            setError(`${ name } is required.`);

            return;
        }

        // Clear field from required list if now valid
        setRequiredFields(prev => prev.filter(field => field !== name));
        setEditAction('save');
        setError(null);
    };

    const handleOnSmsConsentChange = (e) => {
        const { name, checked } = e.target;

        setUser(prev => ({ ...prev, [ name ]: checked }));
        updatePhoneState({ smsConsent: checked });

        setEditAction('save');
    };

    const handleNotificationChange = (e) => {
        const { name, checked } = e.target;
        const [ eventType, method ] = name.split('.');

        setUser(prevUser => ({
            ...prevUser,
            notifications: {
                ...prevUser.notifications,
                [ eventType ]: {
                    ...prevUser.notifications?.[ eventType ],
                    [ method ]: checked
                }
            }
        }));

        setEditAction('save');
    };

    const handlePrivacyChange = (e) => {
        const { name, checked } = e.target;

        setUser(prevUser => ({
            ...prevUser,
            privacy: {
                ...prevUser.privacy,
                [ name ]: checked
            }
        }));

        setEditAction('save');
    };

    // MARK: Edit/Save
    const handleOnEdit = async () => {
        if (editable && editAction === 'save') {
            if (requiredFields.length > 0) {
                setError(`The following fields are required: ${ requiredFields.join(', ') }`);

                return;
            }

            if (userIsAdmin && photo) {
                const result = await uploadPhoto(auth.currentUser.uid, photo, token);

                if (result.error) {
                    setError(result.error);

                    return;
                }

                user.photoURL = result;
            }

            // encode aboutMe before saving
            user.aboutMe = encodeURIComponent(aboutMe.slice(0, 300));

            onSave(user);

            setEditable(false);
        } else if (editable && editAction === 'cancel') {
            setEditable(false);
        } else {
            setEditable(true);
            setEditAction('cancel');
        }
    };

    const handleOnCancel = async () => {
        // Reset all states to original values
        setUser(patroller);
        setEditable(false);
        setEditAction('cancel');
        setError(null);
        setRequiredFields([]);
        setPhoto(null);
        setPreview(patroller?.photoURL || `https://patrol360.dev/uploads/profile_s.png`);
        setPhoneState({
            isValid: false,
            isVerified: patroller.phoneVerified || false,
            smsConsent: patroller.smsConsent || false,
            verificationSent: false,
            verificationCode: '',
            isVerifying: false,
            changeRequested: false,
            changeAllowed: false
        });
    };

    const handleChangePhoneNumber = (e) => {
        const checked = e.target.checked;

        if (checked) {
            updatePhoneState({ changeRequested: true });
        } else {
            updatePhoneState({
                changeRequested: false,
                changeAllowed: false,
                verificationCode: ''
            });
            setUser(prev => ({
                ...prev,
                phoneNumber: patroller.phoneNumber // reset to original
            }));
        }
    };

    // Enhanced phone verification with better error handling
    const handleVerifyPhone = async (method, code, identifier) => {
        // Prevent multiple simultaneous verification attempts
        if (phoneState.isVerifying) {
            return;
        }

        updatePhoneState({ isVerifying: true });

        setError(null);

        try {
            const result = await verifyPhone(auth.currentUser.uid, method, code, identifier, phoneState.smsConsent, token);

            if (result.error) {
                setError(result.error);

                // Handle specific error cases
                if (result.error.includes('rate limit') || result.error.includes('too many')) {
                    updatePhoneState({ rateLimited: true });
                }
            } else if (result.verified) {
                updatePhoneState({
                    isVerified: true,
                    isValid: true,
                    changeRequested: false,
                    changeAllowed: false,
                    verificationCode: '',
                    verificationSent: false
                });

                setUser(prev => ({ ...prev, phoneVerified: true, phoneVerificationCode: null, phoneVerificationExpires: null }));

                setError(null);
            } else if (result.sent) {
                updatePhoneState({ verificationSent: true, isVerified: false });

                setError(null);
            } else {
                setError('Verification failed. Please try again.');
            }
        } catch {
            setError('Verification service unavailable. Please try again later.');
        } finally {
            updatePhoneState({ isVerifying: false });
        }
    };

    // MARK: Render
    return (
        <Accordion expanded={ editable } onChange={ handleOnEdit } sx={ {
            width: '100%',
            background: editable ? 'rgba(255, 255, 255, 0.1)' : 'none',
            boxShadow: 'none',
            '&:before': { display: 'none' }
        } }>
            { editable && (userIsAdmin || userIsSelf) ? (
                <AccordionDetails>
                    <div className={ styles.form }>
                        {/* Profile Picture Section */ }
                        <div className={ styles.profileSection }>
                            <div className={ styles.profilePicture }>
                                <img
                                    src={ preview }
                                    alt="Profile"
                                    onClick={ userIsAdmin ? handleEditPhoto : undefined }
                                    className={ userIsAdmin ? styles.editablePhoto : styles.nonEditablePhoto }
                                />

                                { userIsAdmin && <div className={ styles.photoOverlay } onClick={ handleEditPhoto }>
                                    <CameraAlt />

                                    <span>Change Photo</span>
                                </div> }

                                { userIsAdmin && <input
                                    type="file"
                                    ref={ photoFileRef }
                                    onChange={ handleOnFileChange }
                                    accept="image/*"
                                    hidden
                                /> }
                            </div>
                        </div>

                        {/* Personal Information */ }
                        <Accordion defaultExpanded={ requiredFields.length > 0 } sx={ { boxShadow: 'none', border: '1px solid', borderRadius: '8px', borderColor: 'divider' } }>
                            <AccordionSummary expandIcon={ <ExpandMore /> } sx={ { borderBottom: '1px solid', borderColor: 'divider' } }>
                                <Typography variant="h6">Personal Information</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <Typography>First Name</Typography>

                                        <input
                                            type="text"
                                            id="firstName"
                                            name="firstName"
                                            autoComplete='given-name'
                                            required
                                            value={ user.firstName || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('firstName') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('firstName') && <Alert severity="error" variant='filled'>First name is required.</Alert> }
                                    </div>

                                    <div className={ styles.formField }>
                                        <Typography>Last Name</Typography>

                                        <input
                                            type="text"
                                            id="lastName"
                                            name="lastName"
                                            autoComplete='family-name'
                                            required
                                            value={ user.lastName || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('lastName') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('lastName') && <Alert severity="error" variant='filled'>Last name is required.</Alert> }
                                    </div>
                                </div>

                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <Stack direction="row" alignItems='center' spacing={ 1 }>
                                            <Typography>Start Date</Typography>

                                            <Typography variant="caption" color="text.secondary">(YYYY-MM-DD)</Typography>
                                        </Stack>

                                        <input
                                            type="text"
                                            id="startDate"
                                            name="startDate"
                                            autoComplete='off'
                                            placeholder='e.g. 2023, 2023-01'
                                            required
                                            value={ user.startDate || '' }
                                            onChange={ handleOnChange }
                                        />

                                        { requiredFields.includes('startDate') && <Alert severity="error" variant='filled'>Start date is required. It can be the year (e.g., 2023), the year and month (e.g., 2023-01), or the full date (e.g., 2023-01-15).</Alert> }
                                    </div>

                                    <div className={ styles.formField }>
                                        <Typography>NSP Number</Typography>

                                        <input
                                            type="number"
                                            id="nspNumber"
                                            name="nspNumber"
                                            autoComplete='off'
                                            required
                                            value={ user.nspNumber || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('nspNumber') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('nspNumber') && <Alert severity="error" variant='filled'>NSP number is required.</Alert> }
                                    </div>
                                </div>

                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <Typography>City</Typography>

                                        <input
                                            type="text"
                                            id="city"
                                            name="city"
                                            autoComplete='off'
                                            required
                                            value={ user.city || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('city') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('city') && <Alert severity="error" variant='filled'>City is required.</Alert> }
                                    </div>

                                    <div className={ styles.formField }>
                                        <Typography>Zip Code</Typography>

                                        <input
                                            type="number"
                                            id="zipCode"
                                            name="zipCode"
                                            autoComplete='off'
                                            required
                                            value={ user.zipCode || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('zipCode') ? styles.required : '' }
                                        />

                                        { requiredFields.includes('zipCode') && <Alert severity="error" variant='filled'>Zip code is required.</Alert> }
                                    </div>
                                </div>

                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <Stack direction="row" display='flex' justifyContent='space-between' alignItems="center">
                                            <Typography component="label" htmlFor="aboutMe">About Me</Typography>

                                            <Typography variant="caption" color={ aboutMe.length >= 300 ? "error" : "text.secondary" }>{ aboutMe.length } / 300</Typography>
                                        </Stack>
                                        
                                        <textarea
                                            id="aboutMe"
                                            name="aboutMe"
                                            autoComplete='off'
                                            placeholder='Tell everyone a little about yourself...'
                                            value={ aboutMe }
                                            onChange={ handleAboutMeChange }
                                            rows={ 4 }
                                            className={ styles.aboutMe }
                                        />
                                    </div>
                                </div>
                            </AccordionDetails>
                        </Accordion>

                        {/* Contact Information */ }
                        <Accordion defaultExpanded={ !phoneState.isVerified } sx={ { boxShadow: 'none', border: '1px solid', borderRadius: '8px', borderColor: 'divider' } }>
                            <AccordionSummary expandIcon={ <ExpandMore /> } sx={ { borderBottom: '1px solid', borderColor: 'divider' } }>
                                <Typography variant="h6">Contact Information</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <Typography>Phone Number</Typography>

                                        <input
                                            type="tel"
                                            id="phoneNumber"
                                            name="phoneNumber"
                                            autoComplete='tel'
                                            required
                                            value={ user.phoneNumber || '' }
                                            onChange={ handleOnChange }
                                            className={ requiredFields.includes('phoneNumber') || !user.phoneVerified ? styles.required : user.phoneVerified ? styles.verified : '' }
                                            disabled={ phoneState.isVerified }
                                        />

                                        { requiredFields.includes('phoneNumber') && <Alert severity="error" variant='filled'>Phone number is invalid.</Alert> }
                                        { !requiredFields.includes('phoneNumber') && !user.phoneVerified && (
                                            <Alert severity="warning" variant='filled'>
                                                <AlertTitle>Unverified Phone Number</AlertTitle>
                                                All patrollers must have a verified phone number.
                                            </Alert>
                                        ) }
                                    </div>

                                    <div className={ styles.formField }>
                                        <Typography>Email</Typography>
                                        
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            autoComplete='email'
                                            value={ user.email }
                                            readOnly={ true }
                                            className={ styles.readOnly }
                                        />
                                    </div>
                                </div>

                                { userIsSelf && user.phoneNumber && phoneState.isVerified && (
                                    <Stack spacing={ 2 }>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="changePhoneNumber"
                                                    checked={ phoneState.changeRequested }
                                                    onChange={ handleChangePhoneNumber }
                                                    disabled={ phoneState.changeAllowed }
                                                />
                                            }
                                            label="Change Phone Number"
                                        />

                                        { phoneState.changeRequested && !phoneState.changeAllowed && (
                                            <Alert
                                                severity="warning"
                                                variant='filled'
                                                action={
                                                    <Button
                                                        variant="outlined"
                                                        color="text.secondary"
                                                        size="small"
                                                        onClick={ () => updatePhoneState({ changeAllowed: true, isVerified: false }) }
                                                    >
                                                        proceed
                                                    </Button> }
                                            >
                                                Changing your phone number will require re-verification and SMS consent (if applicable).
                                            </Alert>
                                        ) }
                                    </Stack>
                                ) }

                                { userIsSelf && !user.phoneVerified && phoneState.isValid && <div className={ styles.formRow }>
                                    <div className={ styles.formField }>
                                        <Typography>Verification Code</Typography>
                                        
                                        <Stack direction="row" spacing={ 2 }>
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                id="verificationCode"
                                                name="verificationCode"
                                                value={ phoneState.verificationCode }
                                                onChange={ (e) => updatePhoneState({ verificationCode: e.target.value }) }
                                                autoComplete='one-time-code'
                                                maxLength={ 6 }
                                                className={ styles.verificationCode }
                                            />

                                            <Button
                                                variant="contained"
                                                color="success"
                                                loading={ phoneState.isVerifying }
                                                onClick={ () => handleVerifyPhone('code', phoneState.verificationCode, user.phoneNumber) }
                                                disabled={ phoneState.verificationCode.length !== 6 || !phoneState.isValid }
                                            >
                                                Verify
                                            </Button>
                                        </Stack>
                                    </div>

                                    { !phoneState.verificationSent && phoneState.isValid && <div className={ styles.formField }>
                                        <Typography>Verification Method</Typography>
                                        
                                        <Stack direction="row" spacing={ 2 }>
                                            <Button
                                                variant="contained"
                                                color="success"
                                                loading={ phoneState.isVerifying }
                                                disabled={ !phoneState.isValid }
                                                onClick={ () => handleVerifyPhone('voice', null, user.phoneNumber) }
                                            >
                                                Verify by Call
                                            </Button>

                                            <Tooltip title="SMS Consent Required">
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        loading={ phoneState.isVerifying }
                                                        disabled={ !phoneState.isValid || !user.smsConsent }
                                                        onClick={ () => handleVerifyPhone('sms', null, user.phoneNumber) }
                                                    >
                                                        Verify by Text
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </Stack>

                                        { error && <Alert severity="error" variant='filled' sx={ { mt: 2 } }>{ error }</Alert> }
                                    </div> }
                                </div> }

                                <Divider sx={ { my: 4, borderColor: 'grey.500' } } />

                                {/* SMS Consent */}
                                <Card variant='outlined' sx={ { border: '1px solid', borderColor: 'warning.main' } }>
                                    <CardHeader
                                        title="SMS Consent"
                                        subheader="Consent is required to receive text notifications about shifts, events, emergency alerts, and verification codes."
                                        slotProps={ { title: { color: 'warning.dark' }, subheader: { color: 'warning.light' } } }
                                    />

                                    <CardContent>
                                        <Typography variant="body1" color="info" sx={ { mr: 1 } }>
                                            Consent is needed to verify your phone number via text or to receive SMS alerts.
                                        </Typography>

                                        <Divider sx={ { my: 2 } } />

                                        <CardActions sx={ { display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' } }>
                                            { userIsSelf ? <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={ phoneState.smsConsent }
                                                        onChange={ handleOnSmsConsentChange }
                                                        name="smsConsent"
                                                        color="warning"
                                                        disabled={ !userIsSelf || !user.phoneNumber || !phoneState.isValid }
                                                    />
                                                }
                                                label="I consent to receive SMS messages from Patrol360"
                                                sx={ { color: 'warning.main', backgroundColor: 'rgba(67, 52, 33, 0.63)', borderRadius: '4px', py: 2, px: 3, ml: 0, mr: 0, mb: 2 } }
                                            /> : (
                                                <Alert severity="info" variant='filled' color={ user.smsConsent ? "success" : "info" } >
                                                    <Typography variant="caption" color="text.secondary">User has { user.smsConsent ? 'consented' : 'NOT consented' } to receive SMS messages.</Typography>
                                                </Alert>
                                            ) }
                                        </CardActions>
                                    </CardContent>
                                </Card>
                            </AccordionDetails>
                        </Accordion>

                        {/* MARK: Privacy Settings */ }
                        { phoneState.isVerified && <Accordion defaultExpanded={ !phoneState.isVerified } sx={ { boxShadow: 'none', border: '1px solid', borderRadius: '8px', borderColor: 'divider' } }>
                            <AccordionSummary expandIcon={ <ExpandMore /> } sx={ { borderBottom: '1px solid', borderColor: 'divider' } }>
                                <Typography variant="h6">Privacy</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Typography color='secondary'>Admins can see all information.</Typography>

                                <FormGroup sx={{ mt: 2}}>
                                    <FormControlLabel control={
                                        <Switch
                                            checked={ user.privacy?.email || false }
                                            onChange={ handlePrivacyChange }
                                            name="email"
                                        /> }

                                        label="Allow all users to see your email address."
                                        sx={ {
                                            color: user.privacy?.email ? 'primary.main' : 'gray'
                                        } }
                                    />

                                    <FormControlLabel control={
                                        <Switch
                                            checked={ user.privacy?.phoneNumber || false }
                                            onChange={ handlePrivacyChange }
                                            name="phoneNumber"
                                        /> }

                                        label="Allow all users to see your phone number."
                                        sx={ {
                                            color: user.privacy?.phoneNumber ? 'primary.main' : 'gray'
                                        } }
                                    />

                                    <FormControlLabel control={
                                        <Switch
                                            checked={ user.privacy?.nspNumber || false }
                                            onChange={ handlePrivacyChange }
                                            name="nspNumber"
                                        /> }

                                        label="Allow all users to see your NSP number."
                                        sx={ {
                                            color: user.privacy?.nspNumber ? 'primary.main' : 'gray'
                                        } }
                                    />

                                    <FormControlLabel control={
                                        <Switch
                                            checked={ user.privacy?.city || false }
                                            onChange={ handlePrivacyChange }
                                            name="city"
                                        /> }

                                        label="Allow all users to see your city."
                                        sx={ {
                                            color: user.privacy?.city ? 'primary.main' : 'gray'
                                        } }
                                    />
                                </FormGroup>

                                <Divider sx={ { my: 4, borderColor: 'grey.500' } } />

                                <Link href="https://patrol360.dev/privacy.html" color="info.light" target="_blank" rel="noopener noreferrer">
                                    Patrol360 Privacy and Data Collection Policy
                                </Link>
                            </AccordionDetails>
                        </Accordion> }

                        {/* MARK: Notifications */ }
                        { phoneState.isVerified && <Notifications
                            user={ user }
                            handleOnChange={ handleOnChange }
                            handleNotificationChange={ handleNotificationChange }
                        /> }

                        {/* MARK: Account Settings */ }
                        { userIsAdmin && !userIsSelf && <Accordion defaultExpanded={ false } sx={ { boxShadow: 'none', border: '1px solid', borderRadius: '8px', borderColor: 'divider' } }>
                            <AccordionSummary expandIcon={ <ExpandMore /> } sx={ { borderBottom: '1px solid', borderColor: 'divider' } }>
                                <Typography variant="h6">Account Settings</Typography>
                            </AccordionSummary>

                            <AccordionDetails>
                                <Card elevation={ 0 } sx={ { backgroundColor: 'rgba(0, 0, 0, 0.25)' } }>
                                    <Typography color='secondary.main' sx={ { p: 2, pb: 0 } }>Set this user as an admin</Typography>

                                    <CardContent sx={ { xs: { p: 0 }, sm: { p: 1 } } }>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="admin"
                                                    checked={ !!user.admin }
                                                    onChange={ handleAdminChange }
                                                    color={ 'secondary.main' }
                                                />
                                            }
                                            label="Admin"
                                            sx={ {
                                                color: 'secondary.main',
                                                fontWeight: 600
                                            } }
                                        />

                                        <List sx={ { xs: { p: 0 }, sm: { ml: 6 } } }>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2" color='error'>- full access to all Patrol360 apps</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2">- manage users, roles, and permissions</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2">- create and manage ALL events and shifts</Typography></ListItem>
                                            <ListItem sx={ { p: 0 } }><Typography variant="body2">- create and view all reports and analytics</Typography></ListItem>
                                        </List>
                                    </CardContent>
                                </Card>

                                <Divider sx={ { my: 2 } } />

                                <Card elevation={ 0 } sx={ { backgroundColor: 'rgba(0, 0, 0, 0.25)' } }>
                                    <Typography sx={ { p: 2, pb: 0 } }>Set this user as a scheduler</Typography>

                                    { user.admin &&
                                        <Stack display='flex' flexDirection='column' justifyContent='center' alignItems='center'>
                                            <Alert severity="info" variant='filled' sx={ { m: 2 } }>
                                                Admins have scheduler permissions
                                            </Alert>
                                        </Stack>
                                    }

                                    <CardContent sx={ { xs: { p: 0 }, sm: { p: 1 } } }>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="scheduler"
                                                    checked={ !!user.scheduler && !user.admin }
                                                    onChange={ handleSchedulerChange }
                                                    color={ user.scheduler ? 'primary' : 'default' }
                                                    disabled={ user.admin }
                                                />
                                            }
                                            label="Scheduler"
                                            sx={ {
                                                color: 'primary',
                                                fontWeight: 600,
                                                pb: 2
                                            } }
                                        />

                                        <Stack>
                                            <Typography variant="body2">- can create and manage OWNED events and related shifts</Typography>
                                            <Typography variant="caption" sx={ { ml: 2 } }>(cannot edit events or shifts created by others)</Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                <Divider sx={ { my: 2 } } />

                                <Card elevation={ 0 } sx={ { p: 2, backgroundColor: 'rgba(0, 0, 0, 0.5)' } }>
                                    <Typography color="error" sx={ { mb: 2 } }>Disabling an account prevents all access to the Patrol360 platform.</Typography>

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                name="disabled"
                                                checked={ user.disabled || false }
                                                onChange={ handleOnChange }
                                                color={ user.disabled ? 'error' : 'default' }
                                            />
                                        }
                                        label="Disable Account"
                                        sx={ {
                                            color: '#f44336',
                                            fontWeight: 600
                                        } }
                                    />
                                </Card>
                            </AccordionDetails>
                        </Accordion> }
                    </div>

                    <AccordionActions sx={ { mt: 2 } }>
                        <Button variant="outlined" color="warning" onClick={ handleOnCancel }>Cancel</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={ handleOnEdit }
                            disabled={ !canSave }
                        >
                            Update
                        </Button>
                    </AccordionActions>

                    { error &&
                        <Alert
                            severity="error"
                            variant='filled'
                            action={ <Button color="primary" size="small" onClick={ () => setError(null) }>close</Button> }
                        >
                            { error }
                        </Alert>
                    }
                </AccordionDetails>
            ) : (
                <AccordionSummary
                    expandIcon={ !editable ? (userIsAdmin || userIsSelf) ? <Edit color="primary" /> : (userPrivacyInfo && <Visibility color="primary" />) : (!userIsAdmin || !userIsSelf || !userPrivacyInfo) ? <VisibilityOff color="primary" /> : null }
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <div className={ `${ styles.info } ${ user?.disabled ? styles.disabled : '' }` }>
                        <Avatar variant='rounded' src={ user?.photoURL || '' } alt={ displayName || 'User Avatar' } sx={ { mr: 2 } } />

                        <div className={ styles.details }>
                            <span className={ `${ !displayName?.trim() ? styles.missing : '' }` }>{ displayName || 'No Display Name' }</span>
                            <span className={ `${ !user?.role ? styles.missing : '' }` }>{ (userIsAdmin || userIsSelf || userPrivacyInfo?.role) ? user?.role || 'Role' : '' }</span>
                            <span className={ `${ phoneState.isVerified ? styles.verified : !user.phoneNumber ? styles.required : styles.warning }` }>{ (userIsAdmin || userIsSelf || userPrivacyInfo?.phoneNumber) ? user?.phoneNumber || 'Phone' : '' }</span>
                            <span className={ `${ user?.emailVerified ? styles.verified : styles.required }` }>{ (userIsAdmin || userIsSelf || userPrivacyInfo?.email) ? user?.email || 'Email' : '' }</span>
                        </div>
                    </div>
                </AccordionSummary>
            ) }
        </Accordion >
    );
};